// AP delivery picker, laid out like Zapiet's cart widget: "Local Delivery" / "Store Pickup" cards, then for
// delivery a calendar date field and a time field.
// Markup: snippets/ap-delivery-window.liquid. Settings: Theme settings > "AP delivery time window".
// Saves cart attributes "AP Method" (delivery | pickup), "AP Delivery Date" (YYYY-MM-DD), "AP Delivery Window" (HH:MM-HH:MM).
// With "Require" on, Local Delivery can't go to checkout until a date and a time are chosen; Store Pickup needs neither.
// All dates/times are calculated in the store's time zone (America/Toronto), not the visitor's.

(function () {
  if (customElements.get('ap-delivery-window')) return;

  const ATTR_METHOD = 'AP Method';
  const ATTR_DATE = 'AP Delivery Date';
  const ATTR_WINDOW = 'AP Delivery Window';
  const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  // XB AI Cart Upsell replaces the theme cart drawer; its subtotal row is where we place a copy of the picker.
  const XB_ANCHOR = '.xb_slide_cart_footer_subtotal';
  const XB_CHECKOUT = '.xb-cart-drawer-wrapper button';
  const cartUrl = (path) => `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}${path}`;
  let pendingSave = null;
  // Last known saved choice (from the page's cart, then from each successful save). Used for pickers added later.
  const state = { method: null, date: '', window: '' };
  // The delivery choice made before switching to pickup, so switching back restores it (kept for this visit).
  const LAST_KEY = 'apDwLastDelivery';
  let lastDelivery = (() => {
    try { return JSON.parse(sessionStorage.getItem(LAST_KEY)) || { date: '', window: '' }; } catch (error) { return { date: '', window: '' }; }
  })();
  // Defined before the custom element: customElements.define() upgrades pickers already on the page right away.
  const livePickers = () => [...document.querySelectorAll('ap-delivery-window')].filter((el) => el.ready);
  const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

  // <html class="ap-dw-incomplete"> while Local Delivery still needs a date/time: hides the cart's express-pay
  // buttons (Shop Pay, PayPal...), which would otherwise skip the picker, like Zapiet does.
  function updatePageState() {
    const pickers = livePickers();
    document.documentElement.classList.toggle('ap-dw-incomplete', pickers.length > 0 && !pickers[0].isComplete());
  }

  // "09:00-13:00, 13:00-17:00" -> [{ value: '09:00-13:00', start: 540, end: 780 }]
  function parseWindows(text) {
    return String(text || '')
      .split(',')
      .map((part) => part.trim())
      .map((part) => part.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/))
      .filter(Boolean)
      .map((m) => {
        const start = +m[1] * 60 + +m[2];
        const end = +m[3] * 60 + +m[4];
        const pad = (n) => String(n).padStart(2, '0');
        return { value: `${pad(m[1])}:${m[2]}-${pad(m[3])}:${m[4]}`, start, end };
      })
      .filter((w) => w.end > w.start)
      .sort((a, b) => a.start - b.start);
  }

  // "Sat, Sun" or "6,0" -> Set of weekday numbers (0 = Sunday)
  function parseClosedDays(text) {
    const days = new Set();
    String(text || '').toLowerCase().split(/[\s,]+/).filter(Boolean).forEach((token) => {
      if (/^[0-6]$/.test(token)) days.add(+token);
      const index = DAY_NAMES.indexOf(token.slice(0, 3));
      if (index > -1) days.add(index);
    });
    return days;
  }

  function parseDates(text) {
    return new Set(String(text || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
  }

  // Current date (YYYY-MM-DD) and minutes since midnight in the given time zone.
  function nowIn(timeZone) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).map((p) => [p.type, p.value])
    );
    return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: (+parts.hour % 24) * 60 + +parts.minute };
  }

  function toUTCDate(dateString) {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }

  function addDays(dateString, days) {
    const date = toUTCDate(dateString);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  // "2026-10" -> "2026-11"
  function addMonths(month, n) {
    const [y, m] = month.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1 + n, 1));
    return date.toISOString().slice(0, 7);
  }

  class ApDeliveryWindow extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const data = this.dataset;
      if (state.method === null) {
        state.method = data.savedMethod || '';
        state.date = data.savedDate || '';
        state.window = data.savedWindow || '';
      }
      this.windows = parseWindows(data.windows);
      this.cutoff = parseInt(data.cutoff, 10) || 0;
      this.daysAhead = Math.max(1, parseInt(data.days, 10) || 14);
      this.closedDays = parseClosedDays(data.closed);
      this.blackout = parseDates(data.blackout);
      this.required = data.required !== 'false';
      this.defaultMethod = data.defaultMethod === 'pickup' ? 'pickup' : 'delivery';
      this.timeZone = data.timezone || 'America/Toronto';
      this.locale = data.locale || document.documentElement.lang || 'en';

      this.methodButtons = [...this.querySelectorAll('[data-ap-dw-method]')];
      this.panels = [...this.querySelectorAll('[data-ap-dw-panel]')];
      this.dateToggle = this.querySelector('[data-ap-dw-date-toggle]');
      this.dateText = this.querySelector('[data-ap-dw-date-text]');
      this.calendar = this.querySelector('[data-ap-dw-calendar]');
      this.timeRow = this.querySelector('[data-ap-dw-time-row]');
      this.timeSelect = this.querySelector('[data-ap-dw-time]');
      this.errorEl = this.querySelector('[data-ap-dw-error]');
      this.statusEl = this.querySelector('[data-ap-dw-status]');
      if (!this.dateToggle || !this.timeSelect) return;

      this.methodButtons.forEach((button) => button.addEventListener('click', () => this.onMethod(button.dataset.apDwMethod)));
      // Radio-group keys: arrows move between "Local Delivery" and "Store Pickup".
      this.methodButtons.forEach((button, index) => button.addEventListener('keydown', (event) => {
        const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const next = this.methodButtons[(index + step + this.methodButtons.length) % this.methodButtons.length];
        this.onMethod(next.dataset.apDwMethod);
        next.focus();
      }));
      this.dateToggle.addEventListener('click', () => this.toggleCalendar());
      this.calendar.addEventListener('click', (event) => this.onCalendarClick(event));
      this.calendar.addEventListener('keydown', (event) => this.onCalendarKey(event));
      this.timeSelect.addEventListener('change', () => this.onTimeChange());

      this.ready = true;
      this.build();
      this.hidden = false;
    }

    method() {
      return this.dataset.savedMethod === 'pickup' || this.dataset.savedMethod === 'delivery' ? this.dataset.savedMethod : this.defaultMethod;
    }

    // Days (and their windows) the customer can still choose right now.
    availableDays() {
      const now = nowIn(this.timeZone);
      const days = [];
      for (let offset = 0; days.length < this.daysAhead && offset < this.daysAhead + 31; offset++) {
        const date = addDays(now.date, offset);
        if (this.closedDays.has(toUTCDate(date).getUTCDay()) || this.blackout.has(date)) continue;
        const windows = offset === 0
          ? this.windows.filter((w) => w.start - this.cutoff > now.minutes)
          : this.windows;
        if (windows.length) days.push({ date, offset, windows });
      }
      return days;
    }

    isComplete() {
      if (this.method() === 'pickup' || !this.required || !this.days || !this.days.length) return true;
      const day = this.days.find((d) => d.date === this.dataset.savedDate);
      return !!(day && day.windows.some((w) => w.value === this.dataset.savedWindow));
    }

    build() {
      this.days = this.availableDays();
      this.today = nowIn(this.timeZone).date;
      const savedDate = this.dataset.savedDate || '';
      const savedWindow = this.dataset.savedWindow || '';
      const day = this.days.find((d) => d.date === savedDate);
      const windowOk = !savedWindow || (day && day.windows.some((w) => w.value === savedWindow));
      if (savedDate && (!day || !windowOk)) {
        // The earlier choice has passed (or settings changed): clear it so the order never carries a stale window.
        this.dataset.savedDate = '';
        this.dataset.savedWindow = '';
        this.render();
        this.save(this.method(), '', '', this.dataset.textExpired);
        return;
      }
      this.render();
    }

    render() {
      const method = this.method();
      this.methodButtons.forEach((button) => {
        const selected = button.dataset.apDwMethod === method;
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.classList.toggle('is-selected', selected);
        button.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.apDwPanel !== method; });

      const date = this.dataset.savedDate || '';
      const windowValue = this.dataset.savedWindow || '';
      const day = this.days.find((d) => d.date === date) || null;

      if (!this.days.length) {
        this.toggleCalendar(false);
        this.dateToggle.disabled = true;
        this.dateText.textContent = this.dataset.textNone;
      } else {
        this.dateToggle.disabled = false;
        this.dateText.textContent = day ? this.longDate(day.date) : this.dataset.textDatePlaceholder;
      }
      this.dateText.classList.toggle('is-placeholder', !day);

      // Time options for the chosen day (the field only shows once a day is chosen, like Zapiet).
      this.timeSelect.querySelectorAll('option:not([value=""])').forEach((option) => option.remove());
      (day ? day.windows : []).forEach((w) => {
        const option = document.createElement('option');
        option.value = w.value;
        option.textContent = this.windowLabel(w);
        this.timeSelect.appendChild(option);
      });
      this.timeSelect.value = day && day.windows.some((w) => w.value === windowValue) ? windowValue : '';
      this.timeRow.hidden = !day;
      this.timeRow.classList.toggle('is-placeholder', !this.timeSelect.value);

      if (!this.calendar.hidden) this.renderCalendar();
      // An error already on screen follows the choice: gone once complete, "choose a time" once only the time is missing.
      if (this.isComplete()) this.setError('');
      else if (this.classList.contains('has-error')) this.showMissing(false);
      updatePageState();
    }

    // ---- Calendar -------------------------------------------------------------------------------------------------
    toggleCalendar(force, reveal = true) {
      const open = typeof force === 'boolean' ? force : this.calendar.hidden;
      if (open && !this.days.length) return;
      if (open) {
        const selected = this.dataset.savedDate;
        this.viewMonth = (selected && this.days.some((d) => d.date === selected) ? selected : this.days[0].date).slice(0, 7);
        this.renderCalendar();
      }
      this.calendar.hidden = !open;
      this.dateToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.classList.toggle('is-calendar-open', open);
      // On a phone the date field can sit near the bottom of the screen or the slide cart: bring the calendar into view.
      if (open && reveal) this.calendar.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    renderCalendar() {
      const available = new Map(this.days.map((d) => [d.date, d]));
      const firstMonth = this.days[0].date.slice(0, 7);
      const lastMonth = this.days[this.days.length - 1].date.slice(0, 7);
      if (!this.viewMonth || this.viewMonth < firstMonth) this.viewMonth = firstMonth;
      if (this.viewMonth > lastMonth) this.viewMonth = lastMonth;
      const monthStart = `${this.viewMonth}-01`;
      const gridStart = addDays(monthStart, -toUTCDate(monthStart).getUTCDay());
      const title = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(toUTCDate(monthStart));
      const weekday = new Intl.DateTimeFormat(this.locale, { weekday: 'short', timeZone: 'UTC' });
      const selected = this.dataset.savedDate;

      const head = document.createElement('div');
      head.className = 'ap-dw__cal-head';
      const prev = this.navButton('prev', this.viewMonth <= firstMonth);
      const next = this.navButton('next', this.viewMonth >= lastMonth);
      const titleEl = document.createElement('p');
      titleEl.className = 'ap-dw__cal-title';
      titleEl.textContent = title;
      titleEl.setAttribute('aria-live', 'polite');
      head.append(prev, titleEl, next);

      const grid = document.createElement('div');
      grid.className = 'ap-dw__cal-grid';
      grid.setAttribute('role', 'grid');
      for (let i = 0; i < 7; i++) {
        const label = document.createElement('span');
        label.className = 'ap-dw__cal-weekday';
        label.textContent = weekday.format(toUTCDate(addDays(gridStart, i)));
        grid.appendChild(label);
      }
      // Always six rows so the box doesn't jump between months.
      for (let i = 0; i < 42; i++) {
        const date = addDays(gridStart, i);
        const day = available.get(date);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ap-dw__cal-day';
        button.textContent = String(+date.slice(8));
        button.dataset.date = date;
        if (date.slice(0, 7) !== this.viewMonth) button.classList.add('is-outside');
        if (date === this.today) button.classList.add('is-today');
        if (date === selected) {
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
        }
        const long = this.longDate(date);
        if (day) {
          button.setAttribute('aria-label', date === this.today ? `${long} (${this.dataset.textToday})` : long);
        } else {
          button.disabled = true;
          button.setAttribute('aria-label', `${long} – ${this.dataset.textUnavailable}`);
        }
        button.tabIndex = -1;
        grid.appendChild(button);
      }
      // One day in the grid is reachable with Tab: the chosen one, or the first available one this month.
      const focusTarget = grid.querySelector('.ap-dw__cal-day.is-selected:not(:disabled)') ||
        grid.querySelector('.ap-dw__cal-day:not(:disabled):not(.is-outside)') ||
        grid.querySelector('.ap-dw__cal-day:not(:disabled)');
      if (focusTarget) focusTarget.tabIndex = 0;

      this.calendar.replaceChildren(head, grid);
    }

    navButton(direction, disabled) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `ap-dw__cal-nav ap-dw__cal-nav--${direction}`;
      button.dataset.apDwNav = direction;
      button.disabled = disabled;
      button.setAttribute('aria-label', direction === 'prev' ? this.dataset.textPrev : this.dataset.textNext);
      button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="' +
        (direction === 'prev' ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6') + '"/></svg>';
      return button;
    }

    onCalendarClick(event) {
      const nav = event.target.closest('[data-ap-dw-nav]');
      if (nav && !nav.disabled) {
        this.viewMonth = addMonths(this.viewMonth, nav.dataset.apDwNav === 'prev' ? -1 : 1);
        this.renderCalendar();
        const again = this.calendar.querySelector(`[data-ap-dw-nav="${nav.dataset.apDwNav}"]`);
        (again && !again.disabled ? again : this.calendar.querySelector('.ap-dw__cal-day[tabindex="0"]'))?.focus();
        return;
      }
      const dayButton = event.target.closest('.ap-dw__cal-day');
      if (dayButton && !dayButton.disabled) this.onDatePick(dayButton.dataset.date);
    }

    // Arrow keys move between days, Escape closes the calendar.
    onCalendarKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.toggleCalendar(false);
        this.dateToggle.focus();
        return;
      }
      const current = event.target.closest('.ap-dw__cal-day');
      const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
      if (!current || !step) return;
      event.preventDefault();
      const days = [...this.calendar.querySelectorAll('.ap-dw__cal-day:not(:disabled)')];
      const target = step > 0
        ? days.find((b) => b.dataset.date >= addDays(current.dataset.date, step))
        : [...days].reverse().find((b) => b.dataset.date <= addDays(current.dataset.date, step));
      if (!target) return;
      if (target.dataset.date.slice(0, 7) !== this.viewMonth) {
        this.viewMonth = target.dataset.date.slice(0, 7);
        this.renderCalendar();
      }
      const button = this.calendar.querySelector(`.ap-dw__cal-day[data-date="${target.dataset.date}"]`);
      this.calendar.querySelectorAll('.ap-dw__cal-day').forEach((b) => { b.tabIndex = b === button ? 0 : -1; });
      button.focus();
    }

    // ---- Choices --------------------------------------------------------------------------------------------------
    onMethod(method) {
      if (method === this.method()) return;
      this.toggleCalendar(false);
      if (method === 'pickup') {
        lastDelivery = { date: this.dataset.savedDate || '', window: this.dataset.savedWindow || '' };
        try { sessionStorage.setItem(LAST_KEY, JSON.stringify(lastDelivery)); } catch (error) { /* private mode: in-memory only */ }
        this.save('pickup', '', '');
      } else {
        const day = this.days.find((d) => d.date === lastDelivery.date);
        const windowValue = day && day.windows.some((w) => w.value === lastDelivery.window) ? lastDelivery.window : '';
        this.save('delivery', day ? day.date : '', windowValue);
      }
    }

    onDatePick(date) {
      const day = this.days.find((d) => d.date === date);
      if (!day) return;
      // Keep the same time if it is offered on the new day, otherwise ask again.
      const current = this.dataset.savedWindow || '';
      const keep = day.windows.some((w) => w.value === current) ? current : '';
      this.toggleCalendar(false);
      this.save('delivery', date, keep).then(() => {
        if (!keep && !this.timeRow.hidden) this.timeSelect.focus();
      });
    }

    onTimeChange() {
      const value = this.timeSelect.value;
      // The page may have been open for a while: make sure this window hasn't passed its cutoff since it was drawn.
      const day = this.availableDays().find((d) => d.date === this.dataset.savedDate);
      if (value && (!day || !day.windows.some((w) => w.value === value))) {
        this.build();
        this.setStatus(this.dataset.textExpired);
        return;
      }
      this.save('delivery', this.dataset.savedDate || '', value);
    }

    // Redraw from the given saved choice (used when the cart changed elsewhere or the clock passed a cutoff).
    refresh(method, date, windowValue) {
      if (!this.ready) return;
      this.dataset.savedMethod = method;
      this.dataset.savedDate = date;
      this.dataset.savedWindow = windowValue;
      this.build();
    }

    save(method, date, windowValue, message) {
      this.setStatus(this.dataset.textSaving);
      const attributes = {
        [ATTR_METHOD]: method,
        [ATTR_DATE]: method === 'delivery' ? date : '',
        [ATTR_WINDOW]: method === 'delivery' && date ? windowValue : ''
      };
      // Show the choice straight away; the cart is updated in the background.
      document.querySelectorAll('ap-delivery-window').forEach((el) => el.applySaved(attributes, null));
      const request = fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes })
      })
        .then((response) => {
          if (!response.ok) throw new Error(`cart/update ${response.status}`);
          state.method = attributes[ATTR_METHOD];
          state.date = attributes[ATTR_DATE];
          state.window = attributes[ATTR_WINDOW];
          // A notice (e.g. "no longer available") goes to every copy, so another copy can't wipe it.
          document.querySelectorAll('ap-delivery-window').forEach((el) => el.applySaved(attributes, message || ''));
        })
        .catch(() => {
          document.querySelectorAll('ap-delivery-window').forEach((el) => el.applySaved(
            { [ATTR_METHOD]: state.method || '', [ATTR_DATE]: state.date, [ATTR_WINDOW]: state.window }, null));
          this.setStatus(this.dataset.textError, true);
        });
      const tracked = request.finally(() => { if (pendingSave === tracked) pendingSave = null; });
      pendingSave = tracked;
      return request;
    }

    // Keeps every picker on the page (drawer + cart page + XB copy) in step with what was saved.
    // message: null = keep the current status line, '' = clear it, text = show it.
    applySaved(attributes, message) {
      this.dataset.savedMethod = attributes[ATTR_METHOD] || '';
      this.dataset.savedDate = attributes[ATTR_DATE] || '';
      this.dataset.savedWindow = attributes[ATTR_WINDOW] || '';
      if (!this.ready) return;
      this.render();
      if (message !== null) this.setStatus(message);
    }

    // ---- Checkout guard -------------------------------------------------------------------------------------------
    showMissing(scroll) {
      const missingDate = !this.days.some((d) => d.date === this.dataset.savedDate);
      this.setError(missingDate ? this.dataset.textRequired : this.dataset.textRequiredTime);
      if (!scroll) return;
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (missingDate) {
        // the picker is already being centred on screen, so the calendar doesn't scroll again
        this.toggleCalendar(true, false);
        const day = this.calendar.querySelector('.ap-dw__cal-day[tabindex="0"]');
        if (day) day.focus({ preventScroll: true });
      } else {
        this.timeSelect.focus({ preventScroll: true });
      }
    }

    // ---- Labels ---------------------------------------------------------------------------------------------------
    // "Thu, October 22, 2026" (same style as Zapiet)
    longDate(date) {
      return new Intl.DateTimeFormat(this.locale, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(toUTCDate(date));
    }

    windowLabel(w) {
      const format = new Intl.DateTimeFormat(this.locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
      const at = (minutes) => format.format(new Date(Date.UTC(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)));
      return `${at(w.start)} – ${at(w.end)}`;
    }

    setStatus(text, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text || '';
      this.statusEl.classList.toggle('is-error', !!isError);
    }

    setError(text) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text || '';
      this.errorEl.hidden = !text;
      this.classList.toggle('has-error', !!text);
    }
  }

  customElements.define('ap-delivery-window', ApDeliveryWindow);

  // Place a copy of the picker in the XB slide-out cart, above its subtotal, only while the cart has items that
  // need delivery. XB redraws its drawer, so this runs again on page changes: it re-adds the picker (with the
  // latest choice) if XB removed it, and removes it when the cart no longer needs delivery (e.g. emptied).
  // Shopify is only asked for the cart when a picker is missing or the XB subtotal text changed.
  let xbQueued = false;
  let xbLastSubtotal = null;
  let xbNoDelivery = false;
  let xbForce = false;
  const queueXbSync = () => {
    if (xbQueued) return;
    xbQueued = true;
    queueMicrotask(syncXbCart);
  };
  const xbAnchors = () => [...document.querySelectorAll(XB_ANCHOR)].filter((anchor) => anchor.parentElement);
  async function syncXbCart() {
    try {
      const template = document.getElementById('ApDeliveryWindowTemplate');
      const source = template && template.content.querySelector('ap-delivery-window');
      const anchors = xbAnchors();
      if (!source || !anchors.length) return;
      const missing = anchors.some((anchor) => !anchor.parentElement.querySelector('ap-delivery-window'));
      const subtotal = anchors.map((anchor) => anchor.textContent).join('|');
      if (!xbForce && subtotal === xbLastSubtotal && (!missing || xbNoDelivery)) return;
      xbForce = false;
      xbLastSubtotal = subtotal;
      const cart = await fetch(cartUrl('cart.js'), { cache: 'no-store' }).then((r) => r.json());
      const attrs = cart.attributes || {};
      // Don't overwrite a choice that is still being saved. Pickers already on the page follow the cart too
      // (e.g. the choice was changed in another tab).
      if (!pendingSave) {
        state.method = attrs[ATTR_METHOD] || '';
        state.date = attrs[ATTR_DATE] || '';
        state.window = attrs[ATTR_WINDOW] || '';
        livePickers().forEach((el) => {
          if (el.dataset.savedMethod !== state.method || el.dataset.savedDate !== state.date || el.dataset.savedWindow !== state.window) {
            el.refresh(state.method, state.date, state.window);
          }
        });
      }
      xbNoDelivery = !cart.requires_shipping;
      if (xbNoDelivery) {
        document.querySelectorAll('ap-delivery-window.ap-dw--xb').forEach((el) => el.remove());
        updatePageState();
        return;
      }
      xbAnchors().filter((anchor) => !anchor.parentElement.querySelector('ap-delivery-window')).forEach((anchor) => {
        const picker = source.cloneNode(true);
        picker.classList.add('ap-dw--xb');
        picker.dataset.savedMethod = state.method || '';
        picker.dataset.savedDate = state.date;
        picker.dataset.savedWindow = state.window;
        // Each copy needs its own calendar id for aria-controls.
        const calendar = picker.querySelector('[data-ap-dw-calendar]');
        const toggle = picker.querySelector('[data-ap-dw-date-toggle]');
        if (calendar && toggle) {
          calendar.id = `ApDwCalendar-Xb-${Math.random().toString(36).slice(2, 8)}`;
          toggle.setAttribute('aria-controls', calendar.id);
        }
        anchor.parentElement.insertBefore(picker, anchor);
      });
    } catch (error) {
      // If the cart can't be read, leave the slide cart as it is.
    } finally {
      xbQueued = false;
    }
  }
  if (document.getElementById('ApDeliveryWindowTemplate')) {
    // queueMicrotask (not requestAnimationFrame) so it also runs while the tab is in the background.
    new MutationObserver(queueXbSync).observe(document.body, { childList: true, subtree: true, characterData: true });
    // XB redraws before its cart request finishes, so also re-check once any cart change request has completed.
    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver((list) => {
          if (list.getEntries().some((entry) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(entry.name))) {
            xbForce = true;
            queueXbSync();
          }
        }).observe({ type: 'resource' });
      } catch (error) {
        // Older browsers: the MutationObserver above still keeps the slide cart in step.
      }
    }
    syncXbCart();
  }

  // Re-read the cart when the customer comes back to this tab (the choice may have changed in another tab),
  // and once a minute redraw so windows disappear as their same-day cutoff passes.
  async function syncFromCart() {
    if (pendingSave) return;
    try {
      const cart = await fetch(cartUrl('cart.js'), { cache: 'no-store' }).then((r) => r.json());
      const attrs = cart.attributes || {};
      state.method = attrs[ATTR_METHOD] || '';
      state.date = attrs[ATTR_DATE] || '';
      state.window = attrs[ATTR_WINDOW] || '';
      livePickers().forEach((el) => el.refresh(state.method, state.date, state.window));
    } catch (error) {
      // Leave the pickers as they are if the cart can't be read.
    }
  }
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') syncFromCart(); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) syncFromCart(); });
  setInterval(() => {
    livePickers().forEach((el) => {
      if (pendingSave) return;
      const next = JSON.stringify(el.availableDays().map((d) => [d.date, d.windows.map((w) => w.value)]));
      const current = JSON.stringify(el.days.map((d) => [d.date, d.windows.map((w) => w.value)]));
      if (next !== current) el.refresh(el.dataset.savedMethod || '', el.dataset.savedDate || '', el.dataset.savedWindow || '');
    });
  }, 60000);

  // Checkout buttons (cart page, theme drawer, XB slide cart):
  // - Local Delivery without a date and time: stop, show the message, and open the missing field.
  // - A save still in flight: wait for it so the order gets the choice.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[name="checkout"], a[href$="/checkout"]') ||
      [...document.querySelectorAll(XB_CHECKOUT)].find((b) => b.contains(event.target) && /checkout/i.test(b.textContent));
    if (!button || button.dataset.apDwWaited) return;
    const pickers = livePickers();
    if (pickers.length && !pickers[0].isComplete()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const shown = pickers.find((el) => isVisible(el) && button.closest('.xb-cart-drawer-wrapper, cart-drawer, .drawer') === el.closest('.xb-cart-drawer-wrapper, cart-drawer, .drawer')) ||
        pickers.find(isVisible) || pickers[0];
      pickers.forEach((el) => el.showMissing(el === shown));
      return;
    }
    if (!pendingSave) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pendingSave.finally(() => {
      button.dataset.apDwWaited = '1';
      button.click();
      delete button.dataset.apDwWaited;
    });
  }, true);
})();
